import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { analyticsAPI } from '../../lib/api';

const formatShortDate = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-[#2C3C65] mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: <strong>{entry.value}</strong>
        </p>
      ))}
    </div>
  );
};

const VisitsChart = () => {
  const [data, setData] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    analyticsAPI.getDailyVisits(days)
      .then((r) => setData((r.data || []).map((d) => ({ ...d, label: formatShortDate(d.date) }))))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [days]);

  const totalVisits = data.reduce((s, d) => s + (d.visits || 0), 0);
  const totalPhone = data.reduce((s, d) => s + (d.phone || 0), 0);
  const totalEmail = data.reduce((s, d) => s + (d.email || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-md p-6"
      data-testid="visits-chart"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-xl flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#2C3C65]">Évolution du trafic</h2>
            <p className="text-sm text-[#6777A1]">
              Visites et interactions sur les {days} derniers jours
            </p>
          </div>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                days === d
                  ? 'bg-white text-[#285BBB] shadow-sm'
                  : 'text-[#6777A1] hover:text-[#2C3C65]'
              }`}
              data-testid={`chart-range-${d}`}
            >
              {d} j
            </button>
          ))}
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Visites</p>
          <p className="text-xl font-bold text-blue-900">{totalVisits}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
          <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Clics Tél</p>
          <p className="text-xl font-bold text-emerald-900">{totalPhone}</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Clics Email</p>
          <p className="text-xl font-bold text-amber-900">{totalEmail}</p>
        </div>
      </div>

      {loading ? (
        <div className="h-72 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB]" />
        </div>
      ) : (
        <div className="h-72 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#285BBB" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#285BBB" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6777A1' }}
                interval={days > 14 ? Math.floor(days / 10) : 0}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6777A1' }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(40,91,187,0.05)' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
              <Bar dataKey="visits" name="Visites" fill="url(#gradBar)" radius={[6, 6, 0, 0]} />
              <Line type="monotone" dataKey="phone" name="Tél" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="email" name="Email" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
};

export default VisitsChart;
